<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { toTypedSchema } from '@vee-validate/zod'
import parsePhoneNumber from 'libphonenumber-js'
import * as z from 'zod'
import { Link, UserRoundPlus, HelpCircle } from 'lucide-vue-next'

import { Invite } from '@/types'
import { generateDisplayName } from '@/utils/user'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'


const store = useStore()
const route = useRoute()

const currentMember = computed(() => store.getters['user/getMember'])
const projectId = route.params.project_id
const project = computed(() => store.getters['project/current'])

const isOpen = ref(false)
const isInviteInProgress = ref(false)
const isLinkCopied = ref(false)
const isPersonal = ref(false)
const isIncludeInOrg = ref(false)
const invite = ref<Invite | null>(null)

const inviteLink = computed(() => (invite.value ? urlFromUuid(invite.value.uuid) : null))

const countryCode = ref<string | null | undefined>(null)
const countryCodeToEmoji = computed(() => {
  if (!countryCode.value) return null
  const code = countryCode.value.toUpperCase()
  const codePoints = code.split('').map((char) => char.charCodeAt(0) + 127397)
  return String.fromCodePoint(...codePoints)
})

const phoneFieldSchema = z
  .string()
  .min(1, { message: "Enter a valid email or phone" })
  .transform((value, ctx) => {
    const phoneNumber = parsePhoneNumber(value, { defaultCountry: 'US' })

    if (!phoneNumber?.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter a valid email or phone",
      })
      return z.NEVER
    }

    return phoneNumber.number
  })

const emailFieldSchema = z
  .string()
  .min(1, { message: "Enter a valid email or phone" })
  .email({ message: "Enter a valid email or phone" })

const emailOrPhone = z.union([emailFieldSchema, phoneFieldSchema]).refine(
  (value) => value !== null && value !== undefined && value !== "",
  { message: "Enter a valid email or phone" }
)

const simpleSchema = toTypedSchema(
  z.object({
    role_name: z.string().default('project_editor'),
    include_in_org: z.boolean().default(false),
    app_only: z.boolean().default(false)
  })
)

const fullSchema = toTypedSchema(
  z.object({
    first_name: z.string().max(255).optional(),
    last_name: z.string().max(255).optional(),
    emailOrPhone: emailOrPhone,
    role_name: z.string().default('project_editor'),
    include_in_org: z.boolean().default(false),
    app_only: z.boolean().default(false)
  })
)

const close = () => {
  isOpen.value = false
  invite.value = null
  isInviteInProgress.value = false
  isLinkCopied.value = false
}

watch(isOpen, (newValue) => {
  if (!newValue) {
    invite.value = null
    isInviteInProgress.value = false
    isLinkCopied.value = false
  }
})

const copyLink = () => {
  if (!inviteLink.value) return
  navigator.clipboard.writeText(inviteLink.value)
  isLinkCopied.value = true
  setTimeout(() => {
    isLinkCopied.value = false
  }, 1000)
}

const urlFromUuid = (uuid: string) => {
  return `${location.protocol}//${window.location.host}/invite/${uuid}`
}

const onSubmit = async (values: any) => {
  isInviteInProgress.value = true

  console.log(values)

  const payload = {
    inviter_id: currentMember.value.member_id,
    project_id: projectId,
    role_name: values.role_name,
    email: null,
    phone: null,
    first_name: values.first_name ? values.first_name : null,
    last_name: values.last_name ? values.last_name : null,
    fullname: generateDisplayName(values.first_name || '', values.last_name || '') || null,
    include_in_org: values.include_in_org,
    app_only: values.app_only
  }

  if (values.emailOrPhone) {
    const isEmail = values.emailOrPhone.includes('@')
    payload.email = isEmail ? values.emailOrPhone : null
    payload.phone = !isEmail ? values.emailOrPhone : null
  }

  invite.value = await store.dispatch('project/addInvite', payload)
  isInviteInProgress.value = false
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger as-child>
      <slot>
        <Button variant="ghost" class="h-8 px-2 flex justify-start" data-testid="add-member-button">
          <UserRoundPlus class="w-5 h-5 text-gray-600" />
        </Button>
      </slot>
    </DialogTrigger>

    <DialogContent class="max-w-[350px]">
      <div data-testid="invite-member-dialog">
        <DialogHeader v-if="!invite">
          <DialogTitle class="mb-0.5">Invite a New Member</DialogTitle>
        </DialogHeader>

      <div v-if="!invite" class="flex items-center my-3">
        <Switch :model-value="isPersonal" @update:model-value="isPersonal = $event" data-testid="add-user-details-switch" />
        <Label class="ml-2">Add User Details</Label>
      </div>

      <div v-if="invite" class="text-center pt-3" data-testid="invite-success-section">
        <div class="mb-5">
          <h3 class="font-bold text-lg">Invite Link Created</h3>
          <p class="text-sm">Copy and share the link with the person.</p>

          <div v-if="invite.email" class="mt-3">
            <p class="text-sm">
              A link will also be sent to
              <span class="font-semibold">{{ invite.email }}</span>
            </p>
          </div>
        </div>
        <div v-if="inviteLink">
          <Input :model-value="inviteLink" readonly @click="copyLink" class="cursor-pointer" data-testid="invite-link-input" />
          <div class="mt-2 flex justify-center space-x-2">
            <Button variant="secondary" class="flex items-center" @click="copyLink" data-testid="copy-link-button">
              <Link class="w-3 h-3 mr-1" />
              <span v-if="!isLinkCopied">Copy link</span>
              <span v-else>Link copied!</span>
            </Button>
            <Button variant="ghost" @click="close" data-testid="invite-close-button">
              Close
            </Button>
          </div>
        </div>
      </div>

      <div v-else>
        <Form v-if="isPersonal" class="space-y-3" :validation-schema="fullSchema" @submit="onSubmit" data-testid="invite-member-form-detailed">
          <FormField v-slot="{ componentField }" name="first_name">
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input v-bind="componentField" placeholder="" data-testid="invite-first-name-input" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="last_name">
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input v-bind="componentField" placeholder="" data-testid="invite-last-name-input" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>


          <FormField v-slot="{ componentField }" name="emailOrPhone">
            <FormItem>
              <FormLabel>Email or Phone</FormLabel>
              <FormControl>
                <Input v-bind="componentField" data-testid="invite-email-phone-input" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="role_name">
            <FormItem>
              <FormLabel>Role</FormLabel>
              <FormControl>
                <RadioGroup v-bind="componentField" data-testid="invite-role-selection">
                  <FormItem class="flex items-center space-y-0 gap-x-3">
                    <FormControl>
                      <RadioGroupItem value="project_admin" data-testid="role-admin-radio" />
                    </FormControl>
                    <FormLabel class="font-normal">Administator</FormLabel>
                  </FormItem>
                  <FormItem class="flex items-center space-y-0 gap-x-3">
                    <FormControl>
                      <RadioGroupItem value="project_editor" data-testid="role-editor-radio" />
                    </FormControl>
                    <FormLabel class="font-normal">Edit</FormLabel>
                  </FormItem>
                  <FormItem class="flex items-center space-y-0 gap-x-3">
                    <FormControl>
                      <RadioGroupItem value="project_viewer" data-testid="role-viewer-radio" />
                    </FormControl>
                    <FormLabel class="font-normal">View</FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
            </FormItem>
            <FormMessage />
          </FormField>

          <FormField v-slot="{ value, handleChange }" name="include_in_org">
            <FormItem class="flex items-center space-y-0 space-x-2">
              <FormControl>
                <Switch :model-value="value" @update:model-value="handleChange" data-testid="include-in-org-switch" />
              </FormControl>
              <FormLabel class="font-normal">Add to {{ project.organization_name }} org</FormLabel>
            </FormItem>
          </FormField>

          <FormField v-slot="{ value, handleChange }" name="app_only">
            <FormItem class="flex items-center space-y-0 space-x-2">
              <FormControl>
                <Switch :model-value="value" @update:model-value="handleChange" data-testid="app-only-switch" />
              </FormControl>
              <div class="flex items-center space-x-1">
                <FormLabel class="font-normal">App Only Invite</FormLabel>
                <Popover>
                  <PopoverTrigger as-child>
                    <HelpCircle class="w-4 h-4 text-gray-400 cursor-pointer" />
                  </PopoverTrigger>
                  <PopoverContent>
                    <p class="text-xs">User will still access the dashboard but receive a different email invitation</p>
                  </PopoverContent>
                </Popover>
              </div>
            </FormItem>
          </FormField>

          <DialogFooter>
            <Button type="button" variant="ghost" @click="close" data-testid="invite-close-button">Close</Button>
            <Button type="submit" :disabled="isInviteInProgress" data-testid="invite-create-button">
              <Spinner v-if="isInviteInProgress" class="w-4 h-4" />
              <span v-else>Create</span>
            </Button>
          </DialogFooter>
        </Form>

        <Form v-else class="space-y-3" :validation-schema="simpleSchema" @submit="onSubmit" data-testid="invite-member-form-simple">
          <FormField v-slot="{ componentField }" name="role_name">
            <FormItem>
              <FormLabel>Role</FormLabel>
              <FormControl>
                <RadioGroup v-bind="componentField" data-testid="invite-role-selection-simple">
                  <FormItem class="flex items-center space-y-0 gap-x-3">
                    <FormControl>
                      <RadioGroupItem value="project_admin" data-testid="role-admin-radio-simple" />
                    </FormControl>
                    <FormLabel class="font-normal">Administator</FormLabel>
                  </FormItem>
                  <FormItem class="flex items-center space-y-0 gap-x-3">
                    <FormControl>
                      <RadioGroupItem value="project_editor" data-testid="role-editor-radio-simple" />
                    </FormControl>
                    <FormLabel class="font-normal">Edit</FormLabel>
                  </FormItem>
                  <FormItem class="flex items-center space-y-0 gap-x-3">
                    <FormControl>
                      <RadioGroupItem value="project_viewer" data-testid="role-viewer-radio-simple" />
                    </FormControl>
                    <FormLabel class="font-normal">View</FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
            </FormItem>
            <FormMessage />
          </FormField>

          <FormField v-slot="{ value, handleChange }" name="include_in_org">
            <FormItem class="flex items-center space-y-0 space-x-2">
              <FormControl>
                <Switch :model-value="value" @update:model-value="handleChange" data-testid="include-in-org-switch-simple" />
              </FormControl>
              <FormLabel class="font-normal">Add to {{ project.organization_name }} organization</FormLabel>
            </FormItem>
          </FormField>

          <FormField v-slot="{ value, handleChange }" name="app_only">
            <FormItem class="flex items-center space-y-0 space-x-2">
              <FormControl>
                <Switch :model-value="value" @update:model-value="handleChange" data-testid="app-only-switch-simple" />
              </FormControl>
              <div class="flex items-center space-x-1">
                <FormLabel class="font-normal">Invite App Only</FormLabel>
                <Popover>
                  <PopoverTrigger as-child>
                    <HelpCircle class="w-3 h-3 text-gray-400 cursor-pointer" />
                  </PopoverTrigger>
                  <PopoverContent>
                    <p class="text-xs">User will still access the dashboard but receive a different email template</p>
                  </PopoverContent>
                </Popover>
              </div>
            </FormItem>
          </FormField>

          <DialogFooter>
            <Button type="button" variant="ghost" @click="close" data-testid="invite-close-button-simple">Close</Button>
            <Button type="submit" :disabled="isInviteInProgress" data-testid="invite-create-button-simple">
              <Spinner v-if="isInviteInProgress" class="w-4 h-4" />
              <span v-else>Create</span>
            </Button>
          </DialogFooter>
        </Form>
      </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
